import { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth';
import { DebitosContext } from '../../contexts/debitos';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../../components/AppLayout';
import { UpgradeProModal } from '../../components/UpgradeProModal';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CurrencyInput } from '../../components/ui/currency-input';
import { useCurrency } from '../../contexts/Currency';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { getCustomCategories } from '../../api/customCategory';
import { getDebitDateRange, listDebits } from '../../api/debitos';
import { LayoutGroup, motion } from 'framer-motion';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Wallet,
  Plus,
  Check,
  Trash2,
  Calendar,
  DollarSign,
  AlertCircle,
  Sparkles,
  Edit,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  GripVertical,
  LayoutGrid,
  List as ListIcon,
  Home,
  CreditCard,
  User as UserIcon,
  GraduationCap,
  PiggyBank,
  Car,
  HeartPulse,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { listarDebitos, listarSituacao } from '../../services/lists';
import { getGroup, listGroups } from '../../api/groups';
import isyAvatar from '../../assets/isy-avatar.png';
import isyAttentive from '../../assets/isy-attentive.png';
import isySad from '../../assets/isy-sad.png';
import './style.css';

const localeMap = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
};

const getCurrentCompetencia = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

const isValidMesRef = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}$/.test(value)) return false;

  const [year, month] = value.split('-').map(Number);
  return year >= 1900 && year <= 2100 && month >= 1 && month <= 12;
};

const normalizeRangeStart = (oldestMonth, currentMonth) => {
  if (!isValidMesRef(oldestMonth) || oldestMonth > currentMonth) {
    return currentMonth;
  }

  return oldestMonth;
};

const addMonthsToMesRef = (mesRef, monthOffset) => {
  if (!isValidMesRef(mesRef)) return null;

  const [year, month] = mesRef.split('-').map(Number);
  const zeroBasedMonth = (year * 12) + (month - 1) + monthOffset;
  const nextYear = Math.floor(zeroBasedMonth / 12);
  const nextMonth = (zeroBasedMonth % 12) + 1;

  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
};

const toInputDateValue = (rawDate) => {
  if (!rawDate) return '';

  const rawText = String(rawDate);
  const isoLikeMatch = rawText.match(/^(\d{4,})-(\d{2})-(\d{2})/);
  if (isoLikeMatch) {
    const year = Number(isoLikeMatch[1]);
    if (year < 1900 || year > 2100) return '';
    return `${isoLikeMatch[1]}-${isoLikeMatch[2]}-${isoLikeMatch[3]}`;
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  if (year < 1900 || year > 2100) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const resolveMesRefFromInputDate = (rawDate) => {
  const inputDate = toInputDateValue(rawDate);
  if (!inputDate) return null;
  return inputDate.slice(0, 7);
};

const resolveInstallmentStartMesRef = (expense) => {
  if (isValidMesRef(expense?.dataReferencia)) {
    return expense.dataReferencia;
  }

  const year = Number(expense?.competenciaAno);
  const month = Number(expense?.competenciaMes);
  const competenciaMesRef = `${year}-${String(month).padStart(2, '0')}`;

  if (isValidMesRef(competenciaMesRef)) {
    return competenciaMesRef;
  }

  return resolveMesRefFromInputDate(expense?.dataVencimento);
};

export const getInstallmentInfo = (expense) => {
  if (Number(expense?.contaFixa) !== 2) return null;

  const current = Number(expense?.numeroParcela);
  const total = Number(expense?.totalParcelas);
  if (!Number.isInteger(current) || !Number.isInteger(total)) return null;
  if (current < 1 || total < 1 || current > total) return null;

  return { current, total };
};

function calculateMaxNavigableMonth(expenses, fallbackMonth, currentMonth) {
  const baseMonth = isValidMesRef(fallbackMonth) ? fallbackMonth : getCurrentCompetencia();
  if (!Array.isArray(expenses)) return baseMonth;

  return expenses.reduce((maxMonth, expense) => {
    const recurrenceType = Number(expense?.contaFixa);

    if (recurrenceType === 2) {
      const installmentCount = Number(expense?.quantidadeParcela);
      if (!Number.isInteger(installmentCount) || installmentCount < 1) return maxMonth;

      const startMonth = resolveInstallmentStartMesRef(expense);
      if (!startMonth) return maxMonth;

      const lastInstallmentMonth = addMonthsToMesRef(startMonth, installmentCount - 1);
      if (!lastInstallmentMonth) return maxMonth;

      return lastInstallmentMonth > maxMonth ? lastInstallmentMonth : maxMonth;
    }

    const mesRefDebito = resolveExpenseMesRef(expense);
    if (!mesRefDebito) return maxMonth;

    if (recurrenceType === 0) {
      return maxMonth;
    }

    return mesRefDebito > maxMonth ? mesRefDebito : maxMonth;
  }, baseMonth);
}

const resolveExpenseMesRef = (expense) => {
  if (typeof expense?.dataReferencia === 'string' && /^\d{4}-\d{2}$/.test(expense.dataReferencia)) {
    return expense.dataReferencia;
  }

  const rawDate = expense?.dataVencimento || expense?.dataCadastro || expense?.createdAt;
  if (!rawDate) return null;

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return null;

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getExpenseId = (expense) => {
  const rawId = expense?._id ?? expense?.id;
  if (rawId === null || rawId === undefined) return null;
  return String(rawId);
};
const getParticipantId = (participant) =>
  participant?._id || participant?.id || participant?.groupMemberId || null;

const ACCOUNT_TYPE_PERSONAL = 'Pessoal';
const ACCOUNT_TYPE_GROUP = 'Grupo';
const DIVISION_EQUAL = 'igual';
const DIVISION_CUSTOM = 'customizado';
const GROUP_ELIGIBLE_STATUSES = new Set(['ATIVO', 'NAO_CADASTRADO', 'CONVIDADO_PENDENTE']);

const toBasisPoints = (percentual) => {
  const parsed = Number(percentual);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
};

const buildEqualPercentages = (members) => {
  if (!Array.isArray(members) || members.length < 2) return [];

  const totalBasis = 10000;
  const base = Math.floor(totalBasis / members.length);
  let remainder = totalBasis - (base * members.length);

  const ownerIndex = members.findIndex((member) => member?.papel === 'OWNER');
  const creatorIndex = ownerIndex >= 0 ? ownerIndex : 0;
  const basisByIndex = members.map(() => base);
  let cursor = creatorIndex;

  while (remainder > 0) {
    basisByIndex[cursor] += 1;
    remainder -= 1;
    cursor = (cursor + 1) % members.length;
  }

  return members.map((member, index) => ({
    groupMemberId: String(member._id),
    percentual: basisByIndex[index] / 100
  }));
};

const buildSplitPreview = (valorOriginal, members, percentages) => {
  const total = Number(valorOriginal);
  if (!Number.isFinite(total) || total <= 0) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  if (!Array.isArray(percentages) || percentages.length === 0) return [];

  const totalCents = Math.round(total * 100);
  const byMemberId = new Map(
    percentages.map((item) => [String(item.groupMemberId), { ...item, basisPoints: Math.round(item.percentual * 100) }])
  );

  const floors = [];
  let sumFloors = 0;
  members.forEach((member, index) => {
    const key = String(member._id);
    const percentage = byMemberId.get(key);
    if (!percentage) return;
    const rawCents = (totalCents * percentage.basisPoints) / 10000;
    const floor = Math.floor(rawCents);
    const fractional = rawCents - floor;

    floors.push({
      key,
      floor,
      fractional,
      isOwner: member.papel === 'OWNER',
      originalIndex: index
    });
    sumFloors += floor;
  });

  let remainder = totalCents - sumFloors;
  const order = [...floors].sort((a, b) => {
    if (b.fractional !== a.fractional) return b.fractional - a.fractional;
    if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
    return a.originalIndex - b.originalIndex;
  });
  const extras = new Map();
  let cursor = 0;
  while (remainder > 0 && order.length > 0) {
    const target = order[cursor % order.length];
    extras.set(target.key, (extras.get(target.key) || 0) + 1);
    remainder -= 1;
    cursor += 1;
  }

  return members.map((member) => {
    const key = String(member._id);
    const floor = floors.find((item) => item.key === key);
    const cents = (floor?.floor || 0) + (extras.get(key) || 0);
    const percentual = byMemberId.get(key)?.percentual || 0;

    return {
      groupMemberId: key,
      nome: member.nome || member.email,
      percentual,
      valorCalculado: cents / 100
    };
  });
};

const CATEGORY_VISUALS = {
  Casa: { icon: Home, bg: '#EEF2FF', color: '#4F46E5' },
  'Dívida': { icon: CreditCard, bg: '#FFE4E6', color: '#E11D48' },
  Divida: { icon: CreditCard, bg: '#FFE4E6', color: '#E11D48' },
  Pessoal: { icon: UserIcon, bg: '#FFEDD5', color: '#EA580C' },
  Educação: { icon: GraduationCap, bg: '#F3E8FF', color: '#7C3AED' },
  Educacao: { icon: GraduationCap, bg: '#F3E8FF', color: '#7C3AED' },
  Investimentos: { icon: PiggyBank, bg: '#DCFCE7', color: '#16A34A' },
  Transporte: { icon: Car, bg: '#FEF3C7', color: '#D97706' },
  'Saúde': { icon: HeartPulse, bg: '#CCFBF1', color: '#0F766E' },
  Saude: { icon: HeartPulse, bg: '#CCFBF1', color: '#0F766E' },
  Responsabilidade: { icon: AlertCircle, bg: '#FFE4E6', color: '#E11D48' },
};

const getCategoryVisual = (category) => {
  if (!category) return { icon: Tag, bg: '#F1F5F9', color: '#475569' };
  const direct = CATEGORY_VISUALS[category];
  if (direct) return direct;
  const normalized = String(category)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  const found = Object.entries(CATEGORY_VISUALS).find(([key]) =>
    key
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase() === normalized
  );
  return found ? found[1] : { icon: Tag, bg: '#F1F5F9', color: '#475569' };
};

const STATUS_KEYS = {
  Pago: 'paid',
  Pendente: 'pending',
  Atrasado: 'overdue',
};

function SortableExpenseCard({
  expense,
  expenseId,
  isOwner,
  isGroupExpense,
  participacoes,
  installmentInfo,
  dragEnabled = true,
  t,
  formatCurrency,
  formatDueDate,
  getRecurrenceLabel,
  onEdit,
  onTogglePay,
  onDelete,
  renderParticipations,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: expenseId, disabled: !dragEnabled });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const visual = getCategoryVisual(expense.categoria);
  const CategoryIcon = visual.icon;
  const statusKey = STATUS_KEYS[expense.situacao] || 'pending';

  return (
    <motion.div
      layout
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      className="expense-card-shell"
    >
      <article
        ref={setNodeRef}
        style={dragStyle}
        className={`expense-card expense-card--${statusKey}${isDragging ? ' is-dragging' : ''}${dragEnabled ? '' : ' expense-card--no-drag'}`}
        data-status={statusKey}
      >
        <button
          type="button"
          ref={dragEnabled ? setActivatorNodeRef : null}
          className={`expense-card__handle${dragEnabled ? '' : ' expense-card__handle--hidden'}`}
          aria-label="Reordenar"
          aria-hidden={!dragEnabled}
          tabIndex={dragEnabled ? 0 : -1}
          {...(dragEnabled ? attributes : {})}
          {...(dragEnabled ? listeners : {})}
        >
          <GripVertical className="h-4 w-4" />
        </button>

      <div className="expense-card__identity">
        <div
          className="expense-card__icon"
          style={{ background: visual.bg, color: visual.color }}
        >
          <CategoryIcon className="h-4 w-4" />
        </div>
        <div className="expense-card__title-block">
          <div className="expense-card__title-row">
            <span className="expense-card__title">
              {expense.descricao}
              {installmentInfo ? ` ${installmentInfo.current}/${installmentInfo.total}` : ''}
            </span>
            {isGroupExpense && (
              <Badge variant="outline" className="expense-card__group-badge">
                {t('group')}
              </Badge>
            )}
          </div>
          <span className="expense-card__category">{expense.categoria}</span>
        </div>
      </div>

      <div className="expense-card__col">
        <span className="expense-card__col-label">{t('yourShare')}</span>
        <span className="expense-card__col-value">{formatCurrency(expense.valor)}</span>
      </div>

      <div className="expense-card__col">
        <span className="expense-card__col-label">{t('totalAmountOriginal')}</span>
        <span className="expense-card__col-value">
          {formatCurrency(expense.valorOriginal || expense.valor)}
        </span>
      </div>

      <div className="expense-card__col">
        <span className="expense-card__col-label">{t('dueDate')}</span>
        <span className="expense-card__col-value">{formatDueDate(expense.dataVencimento)}</span>
      </div>

      <div className="expense-card__col">
        <span className="expense-card__col-label">{t('status')}</span>
        <span
          className="expense-card__status-wrap"
          data-tooltip={statusKey === 'paid' ? t('clickToUnpayHint') : t('clickToPayHint')}
        >
          <button
            type="button"
            className={`expense-card__status-btn expense-card__status-btn--${statusKey}`}
            onClick={() => expenseId && onTogglePay(expenseId)}
            disabled={!expenseId || !isOwner}
            aria-label={statusKey === 'paid' ? t('unmarkAsPaid') : t('markExpenseAsPaid')}
            title={statusKey === 'paid' ? t('clickToUnpayHint') : t('clickToPayHint')}
          >
            {t(statusKey)}
          </button>
        </span>
      </div>

      <div className="expense-card__col">
        <span className="expense-card__col-label">{t('recurrence')}</span>
        <span className="expense-card__col-value">{getRecurrenceLabel(expense)}</span>
      </div>

      <div className="expense-card__actions">
        {isOwner && (
          <>
            <button
              type="button"
              className="expense-card__action expense-card__action--edit"
              onClick={() => onEdit(expense)}
              aria-label={t('editExpense')}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="expense-card__action expense-card__action--delete"
              onClick={() => expenseId && onDelete(expense)}
              aria-label={t('deleteExpense')}
              disabled={!expenseId}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

        {isGroupExpense && participacoes.length > 0 && (
          <div className="expense-card__participations">
            {renderParticipations(expense, participacoes, expenseId)}
          </div>
        )}
      </article>
    </motion.div>
  );
}

function Debits() {
  const { user } = useContext(AuthContext);
  const {
    saveDebitos,
    updateDebitsValues,
    excluirDebits,
    debitos,
    getDebitos,
    payDebito,
    pendingApprovals,
    getPendingApprovals,
    approveParticipation,
    rejectParticipation
  } = useContext(DebitosContext);
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();

  const [upgradeProOpen, setUpgradeProOpen] = useState(false);
  const [userOrderById, setUserOrderById] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('default');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form states
  const [categoria, setCategoria] = useState('Casa');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [situacao, setSituacao] = useState('Pendente');
  const [contaFixa, setContaFixa] = useState('1');
  const [vdata, setData] = useState('');
  const [qtdParcela, setQtdParcela] = useState('');
  const [tipoConta, setTipoConta] = useState(ACCOUNT_TYPE_PERSONAL);
  const [formaDivisao, setFormaDivisao] = useState(DIVISION_EQUAL);
  const [grupoId, setGrupoId] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [customPercentuais, setCustomPercentuais] = useState([]);
  const listSituacao = listarSituacao();

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState('todas');
  const [sortDirection, setSortDirection] = useState('asc');
  const [competencia, setCompetencia] = useState(getCurrentCompetencia);
  const [oldestMonth, setOldestMonth] = useState(getCurrentCompetencia);
  const [isLoadingDebitos, setIsLoadingDebitos] = useState(false);

  // Categorias
  const [categoriasPadrao] = useState([]);
  const [categoriasCustom] = useState([]);
  const [categoriasDropdown, setCategoriasDropdown] = useState([]);

  const refreshDebitDateRange = useCallback(async () => {
    try {
      const data = await getDebitDateRange();
      const fallbackMonth = getCurrentCompetencia();
      const oldest = data?.oldest;
      setOldestMonth(isValidMesRef(oldest) && oldest <= fallbackMonth ? oldest : fallbackMonth);
    } catch {
      setOldestMonth(getCurrentCompetencia());
    }
  }, []);

  const currentMonth = getCurrentCompetencia();
  const nextMonth = addMonthsToMesRef(currentMonth, 1) || currentMonth;
  const maxNavigableMonth = useMemo(
    () => calculateMaxNavigableMonth(debitos, nextMonth, currentMonth),
    [debitos, nextMonth, currentMonth]
  );

  const monthRange = useMemo(() => {
    const dataStart = normalizeRangeStart(oldestMonth, currentMonth);
    // Permite voltar até 24 meses no passado mesmo sem dados,
    // ou até o mês mais antigo com dados, o que for menor.
    const navigableStart = addMonthsToMesRef(currentMonth, -24) || dataStart;
    const start = navigableStart < dataStart ? navigableStart : dataStart;

    return {
      start,
      end: maxNavigableMonth,
    };
  }, [oldestMonth, currentMonth, maxNavigableMonth]);

  const isCompetenciaInRange =
    isValidMesRef(competencia) &&
    competencia >= monthRange.start &&
    competencia <= monthRange.end;

  const selectedMesRef = isCompetenciaInRange ? competencia : currentMonth;

  const competenciaFormatada = useMemo(() => {
    const [ano, mes] = selectedMesRef.split('-').map(Number);
    const langKey = (language || 'pt').split('-')[0];

    try {
      const formatted = new Intl.DateTimeFormat(localeMap[langKey] || 'pt-BR', {
        month: 'long',
        year: 'numeric'
      }).format(new Date(ano, mes - 1, 1));
      // Capitaliza apenas a primeira letra (em PT-BR meses vêm em minúscula).
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return selectedMesRef;
    }
  }, [selectedMesRef, language]);

  const competenciaOptions = useMemo(() => {
    const langKey = (language || 'pt').split('-')[0];
    const localeId = localeMap[langKey] || 'pt-BR';
    const formatLabel = (year, month) => {
      const formatted = new Intl.DateTimeFormat(localeId, {
        month: 'long',
        year: 'numeric'
      }).format(new Date(year, month - 1, 1));
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const options = [];
    let value = monthRange.start;

    while (isValidMesRef(value) && value <= monthRange.end) {
      const [year, month] = value.split('-').map(Number);
      options.push({ value, label: formatLabel(year, month) });
      value = addMonthsToMesRef(value, 1);
    }

    if (options.length === 0) {
      const [year, month] = currentMonth.split('-').map(Number);
      options.push({ value: currentMonth, label: formatLabel(year, month) });
    }

    return options.reverse();
  }, [monthRange.start, monthRange.end, currentMonth, language]);

  useEffect(() => {
    if (!isCompetenciaInRange) {
      setCompetencia(currentMonth);
    }
  }, [isCompetenciaInRange, currentMonth]);

  const debitosFiltrados = useMemo(() => {
    const debitosDoMesSelecionado = debitos.filter((debito) => {
      const recurrenceType = Number(debito?.contaFixa);

      // FIXO: aparece em todos os meses >= mês de origem
      if (recurrenceType === 0) {
        const mesRefDebito = resolveInstallmentStartMesRef(debito);
        if (!mesRefDebito) return false;

        return selectedMesRef >= mesRefDebito;
      }

      // PARCELADO
      if (recurrenceType === 2) {
        const total = Number(debito?.quantidadeParcela);
        if (!Number.isInteger(total) || total < 1) return false;

        const start = resolveInstallmentStartMesRef(debito);
        if (!start) return false;

        const end = addMonthsToMesRef(start, total - 1);
        if (!end) return false;

        return selectedMesRef >= start && selectedMesRef <= end;
      }

      // VARIÁVEL: aparece apenas no mês de origem
      const mesRefDebito = resolveExpenseMesRef(debito);
      if (!mesRefDebito) return false;

      return mesRefDebito === selectedMesRef;
    });

    if (filtroSituacao === 'todas') return debitosDoMesSelecionado;

    return debitosDoMesSelecionado.filter(
      (debito) => debito.situacao?.toLowerCase() === filtroSituacao
    );
  }, [debitos, filtroSituacao, selectedMesRef]);

  // Aplica reorder local do usuário (DnD) preservando ordem original
  // como fallback. Não persiste no backend.
  const applyUserOrder = useCallback((items, zoneKey) => {
    const order = userOrderById[zoneKey];
    if (!Array.isArray(order) || order.length === 0) return items;

    const byId = new Map(items.map((item) => [getExpenseId(item), item]));
    const ordered = [];
    order.forEach((id) => {
      if (byId.has(id)) {
        ordered.push(byId.get(id));
        byId.delete(id);
      }
    });
    byId.forEach((item) => ordered.push(item));
    return ordered;
  }, [userOrderById]);

  const sortItems = useCallback((items) => {
    if (!sortBy || sortBy === 'default') return items;

    const sortStatusRank = { Atrasado: 0, Pendente: 1, Pago: 2 };
    const recurrenceRank = { 0: 'fixed', 1: 'variable', 2: 'installment' };
    const dir = sortDirection === 'desc' ? -1 : 1;

    const next = [...items];
    next.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'dueDate': {
          const da = a.dataVencimento ? new Date(a.dataVencimento).getTime() : Infinity;
          const db = b.dataVencimento ? new Date(b.dataVencimento).getTime() : Infinity;
          cmp = da - db;
          break;
        }
        case 'amount':
          cmp = Number(a.valor || 0) - Number(b.valor || 0);
          break;
        case 'category':
          cmp = String(a.categoria || '').localeCompare(
            String(b.categoria || ''), undefined, { sensitivity: 'base' });
          break;
        case 'description':
          cmp = String(a.descricao || '').localeCompare(
            String(b.descricao || ''), undefined, { sensitivity: 'base' });
          break;
        case 'status': {
          const ra = sortStatusRank[a.situacao] ?? 3;
          const rb = sortStatusRank[b.situacao] ?? 3;
          cmp = ra - rb;
          break;
        }
        case 'recurrence':
          cmp = String(recurrenceRank[a.contaFixa] || '').localeCompare(
            String(recurrenceRank[b.contaFixa] || ''));
          break;
        default:
          cmp = 0;
      }
      return cmp * dir;
    });
    return next;
  }, [sortBy, sortDirection]);

  const isUnsorted = sortBy === 'default';
  const isUnfiltered = filtroSituacao === 'todas';
  const useZones = isUnsorted && isUnfiltered;
  const dragEnabled = useZones; // DnD só faz sentido em ordem manual+sem filtro

  const pendingItems = useMemo(() => {
    if (!useZones) return [];
    return applyUserOrder(
      debitosFiltrados.filter((d) => d.situacao !== 'Pago'),
      'pending',
    );
  }, [debitosFiltrados, applyUserOrder, useZones]);

  const paidItems = useMemo(() => {
    if (!useZones) return [];
    return applyUserOrder(
      debitosFiltrados.filter((d) => d.situacao === 'Pago'),
      'paid',
    );
  }, [debitosFiltrados, applyUserOrder, useZones]);

  const flatItems = useMemo(() => {
    if (useZones) return [];
    return isUnsorted
      ? applyUserOrder(debitosFiltrados, 'flat')
      : sortItems(debitosFiltrados);
  }, [debitosFiltrados, applyUserOrder, sortItems, useZones, isUnsorted]);

  const splitMembers = useMemo(
    () => groupMembers.filter((member) => GROUP_ELIGIBLE_STATUSES.has(member.status)),
    [groupMembers]
  );

  const equalPercentuais = useMemo(
    () => buildEqualPercentages(splitMembers),
    [splitMembers]
  );

  const effectivePercentuais = useMemo(() => {
    if (formaDivisao === DIVISION_EQUAL) {
      return equalPercentuais;
    }

    return customPercentuais.map((item) => ({
      groupMemberId: String(item.groupMemberId),
      percentual: Number(item.percentual)
    }));
  }, [formaDivisao, equalPercentuais, customPercentuais]);

  const totalPercentual = useMemo(() => (
    effectivePercentuais.reduce((total, item) => total + (Number(item.percentual) || 0), 0)
  ), [effectivePercentuais]);

  const previewParticipacoes = useMemo(
    () => buildSplitPreview(valor, splitMembers, effectivePercentuais),
    [valor, splitMembers, effectivePercentuais]
  );

useEffect(() => {
  if (!user?.id) return;

  async function loadCategorias() {
    try {
      // categorias padrão do sistema
      const padrao = listarDebitos().map(c => c.name);

      // categorias custom do backend
      const customResponse = await getCustomCategories(user.id);
      const custom = customResponse.map(c => c.descricao);

      setCategoriasDropdown([...padrao, ...custom]);
    } catch (err) {
      console.error('Erro ao carregar categorias', err);
    }
  }

  loadCategorias();
}, [user?.id]);

useEffect(() => {
  if (!user?.id) return;

  refreshDebitDateRange();
}, [user?.id, refreshDebitDateRange]);

useEffect(() => {
  if (!user?.id) return;

  let mounted = true;

  async function loadDebitosByMonth() {
    setIsLoadingDebitos(true);
    setLoadError(false);
    try {
      // Faz uma chamada direta para detectar falha de rede/CORS/rate-limit;
      // o context engole erros e devolve [], o que mascara o problema.
      await listDebits(user.id, { mesRef: selectedMesRef });
      await Promise.all([
        getDebitos(user.id, { mesRef: selectedMesRef }),
        getPendingApprovals({ mesRef: selectedMesRef })
      ]);
    } catch {
      if (mounted) setLoadError(true);
    } finally {
      if (mounted) {
        setIsLoadingDebitos(false);
      }
    }
  }

  loadDebitosByMonth();

  return () => {
    mounted = false;
  };
}, [user?.id, selectedMesRef, getDebitos, getPendingApprovals]);

useEffect(() => {
  if (!user?.id) return;

  async function loadGroupsForDebit() {
    try {
      const groups = await listGroups();
      setAvailableGroups(groups);
    } catch (error) {
      console.error('Erro ao carregar grupos para débito', error);
    }
  }

  loadGroupsForDebit();
}, [user?.id]);

useEffect(() => {
  if (!grupoId || tipoConta !== ACCOUNT_TYPE_GROUP) {
    setGroupMembers([]);
    return;
  }

  async function loadGroupMembers() {
    try {
      const detail = await getGroup(grupoId);
      const members = Array.isArray(detail?.members) ? detail.members : [];
      setGroupMembers(members);
      setCustomPercentuais((prev) => {
        if (prev.length > 0) return prev;
        return buildEqualPercentages(members.filter((member) => GROUP_ELIGIBLE_STATUSES.has(member.status)));
      });
    } catch (error) {
      setGroupMembers([]);
      toast.error(
        error.response?.data?.error ||
          t('groupMembersLoadError')
      );
    }
  }

  loadGroupMembers();
}, [grupoId, tipoConta, language, t]);


      useEffect(() => {
        const customFormatadas = categoriasCustom.map(c => c.descricao);

        setCategoriasDropdown([
          ...categoriasPadrao,
          ...customFormatadas,
        ]);
      }, [categoriasPadrao, categoriasCustom]);       
          

  // Calcular métricas
  const metrics = useMemo(() => {
    const receita = user?.receita || 0;
    const totalPago = debitos
      .filter(d => d.situacao === 'Pago')
      .reduce((acc, d) => acc + parseFloat(d.valor || 0), 0);

    const totalPendente = debitos
      .filter(d => d.situacao === 'Pendente')
      .reduce((acc, d) => acc + parseFloat(d.valor || 0), 0);

    const totalAtrasado = debitos
      .filter(d => d.situacao === 'Atrasado')
      .reduce((acc, d) => acc + parseFloat(d.valor || 0), 0);

    const totalEssencial = debitos
      .filter(d => ['Casa', 'Dívida', 'Responsabilidade'].includes(d.categoria))
      .reduce((acc, d) => acc + parseFloat(d.valor || 0), 0);

    const totalGastos = debitos
      .reduce((acc, d) => acc + parseFloat(d.valor || 0), 0);

    return {
      receita,
      totalPago,
      totalPendente,
      totalAtrasado,
      totalEssencial,
      totalGastos
    };
  }, [debitos, user]);

  const { format: formatCurrency } = useCurrency();
  // Análise IA
  const getAIAnalysis = () => {
    const percentualGastos = metrics.receita > 0 ? (metrics.totalGastos / metrics.receita) * 100 : 0;
    const saldoRestante = metrics.receita - metrics.totalGastos;
    const deficit = Math.abs(saldoRestante);

    // Crítico: Gastos ultrapassam a receita
    if (percentualGastos > 100) {
      return {
        tipo: 'critico',
        tone: 'critical',
        avatar: isySad,
        titulo: t('criticalExpensesTitle'),
        mensagem: t('criticalExpensesMessage', {
          expenses: formatCurrency(metrics.totalGastos),
          income: formatCurrency(metrics.receita),
          deficit: formatCurrency(deficit),
          percentage: percentualGastos.toFixed(0),
        }),
        cta: null,
      };
    }
    // Alerta: Contas atrasadas
    if (metrics.totalAtrasado > 0) {
      return {
        tipo: 'alerta',
        tone: 'alert',
        avatar: isyAttentive,
        titulo: t('lateBillsTitle'),
        mensagem: t('lateBillsMessage', {
          lateAmount: formatCurrency(metrics.totalAtrasado),
        }),
        cta: null,
      };
    }

    // Cuidado: Gastos acima de 80%
    if (percentualGastos > 80) {
      return {
        tipo: 'cuidado',
        tone: 'caution',
        avatar: isyAttentive,
        titulo: t('expensesAboveIdealTitle'),
        mensagem: t('expensesAboveIdealMessage', {
          expenses: formatCurrency(metrics.totalGastos),
          percentage: percentualGastos.toFixed(0),
          remaining: formatCurrency(saldoRestante),
        }),
        cta: null,
      };
    }
    // Positivo: Gastos controlados
    const investSuggestion = saldoRestante > 0
      ? Math.max(100, Math.floor((saldoRestante * 0.1) / 100) * 100)
      : 0;
    return {
      tipo: 'positivo',
      tone: 'positive',
      avatar: isyAvatar,
      titulo: t('healthyFinancialManagementTitle'),
      mensagem: t('healthyFinancialManagementMessage', {
        expenses: formatCurrency(metrics.totalGastos),
        percentage: percentualGastos.toFixed(0),
        remaining: formatCurrency(saldoRestante),
      }),
      cta: investSuggestion > 0
        ? {
            kind: 'invest',
            label: t('investSuggestionCTA', { amount: formatCurrency(investSuggestion) }),
          }
        : null,
    };
  };

  const aiAnalysis = getAIAnalysis();

  const openDialog = (expense = null) => {
    if (expense) {
      const isGroupExpense = expense.tipoConta === ACCOUNT_TYPE_GROUP || Boolean(expense.pagamentoGrupo);
      const baseAmount = isGroupExpense
        ? Number(expense.valorOriginal ?? expense.valor)
        : Number(expense.valor);

      setIsEditMode(true);
      setEditId(getExpenseId(expense));
      setCategoria(expense.categoria);
      setDescricao(expense.descricao);
      setValor(Number.isFinite(baseAmount) ? baseAmount : '');
      setSituacao(expense.situacao);
      setContaFixa(expense.contaFixa?.toString() || '1');
      setData(toInputDateValue(expense.dataVencimento));
      setQtdParcela(expense.quantidadeParcela || '');
      setTipoConta(isGroupExpense ? ACCOUNT_TYPE_GROUP : ACCOUNT_TYPE_PERSONAL);
      setGrupoId(expense.grupoId ? String(expense.grupoId) : '');
      setFormaDivisao(
        expense.formaDivisao === DIVISION_CUSTOM ? DIVISION_CUSTOM : DIVISION_EQUAL
      );
      setCustomPercentuais(
        Array.isArray(expense.participacoes)
          ? expense.participacoes.map((item) => ({
            groupMemberId: String(item.groupMemberId),
            percentual: Number(item.percentual)
          }))
          : []
      );
    } else {
      setIsEditMode(false);
      setEditId(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setCategoria('Casa');
    setDescricao('');
    setValor('');
    setSituacao('Pendente');
    setContaFixa('1');
    setData('');
    setQtdParcela('');
    setTipoConta(ACCOUNT_TYPE_PERSONAL);
    setFormaDivisao(DIVISION_EQUAL);
    setGrupoId('');
    setGroupMembers([]);
    setCustomPercentuais([]);
  };

  const reloadSelectedMonth = async () => {
    if (!user?.id) return;
    await Promise.all([
      getDebitos(user.id, { mesRef: selectedMesRef }),
      getPendingApprovals({ mesRef: selectedMesRef })
    ]);
  };

  const handleCompetenciaChange = (nextCompetencia) => {
    if (
      !isValidMesRef(nextCompetencia) ||
      nextCompetencia < monthRange.start ||
      nextCompetencia > monthRange.end
    ) {
      setCompetencia(currentMonth);
      return;
    }

    setCompetencia(nextCompetencia);
  };

  const changeCompetenciaBy = (monthOffset) => {
    const nextCompetencia = addMonthsToMesRef(selectedMesRef, monthOffset);

    if (
      !nextCompetencia ||
      nextCompetencia < monthRange.start ||
      nextCompetencia > monthRange.end
    ) {
      return;
    }

    setCompetencia(nextCompetencia);
  };

  const canGoToPreviousMonth = selectedMesRef > monthRange.start;
  const canGoToNextMonth = selectedMesRef < monthRange.end;

  const handleSubmit = async () => {
    if (!descricao || valor == null || valor === '') {
      toast.error(t('fillRequiredFields'));
    }

    const amountValue = Number(valor);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('invalidAmount'));
      return;
    }

    if (vdata) {
      const normalizedDate = toInputDateValue(vdata);
      if (!normalizedDate) {
        toast.error(t('invalidDueDate'));
        return;
      }
    }

    const contaFixaNumber = Number(contaFixa);
    const contaFixaNormalizada = [0, 1, 2].includes(contaFixaNumber) ? contaFixaNumber : 1;

    if (contaFixaNormalizada === 2) {
      const parcelas = parseInt(qtdParcela, 10);
      if (!Number.isInteger(parcelas) || parcelas < 1) {
        toast.error(t('enterInstallments'));
        return;
      }
    }

    const dataVencimentoNormalizada = toInputDateValue(vdata);
    // Fixa em meio-dia UTC pra evitar off-by-one ao exibir em qualquer timezone.
    // Mongoose vai casttar essa string ISO direto.
    const dataVencimentoPayload = dataVencimentoNormalizada
      ? `${dataVencimentoNormalizada}T12:00:00.000Z`
      : null;
    const mesRefFromDueDate = resolveMesRefFromInputDate(vdata);
    const mesRefReferencia = mesRefFromDueDate || selectedMesRef;
    const [competenciaAno, competenciaMes] = mesRefReferencia.split('-').map(Number);

    const isGroupFlow = tipoConta === ACCOUNT_TYPE_GROUP;
    const isEditingGroup = isEditMode && isGroupFlow;

    if (isGroupFlow) {
      if (!grupoId) {
        toast.error(t('selectGroup'));
        return;
      }

      if (splitMembers.length < 2) {
        toast.error(t('minimumGroupParticipants'));
        return;
      }

      if (formaDivisao === DIVISION_CUSTOM) {
        const hasInvalidPercentage = effectivePercentuais.some((item) => !toBasisPoints(item.percentual));
        if (hasInvalidPercentage) {
         toast.error(t('percentagesGreaterThanZero'));
          return;
        }

        if (Math.abs(totalPercentual - 100) > 0.0001) {
          toast.error(t('percentagesMustTotal100'));
          return;
        }
      }
    }

    const commonData = {
      userId: user.id,
      categoria,
      descricao,
      situacao,
      contaFixa: contaFixaNormalizada,
      dataVencimento: dataVencimentoPayload,
      quantidadeParcela: contaFixaNormalizada === 2 && qtdParcela ? parseInt(qtdParcela, 10) : null,
      dataReferencia: mesRefReferencia,
      competenciaAno: Number.isFinite(competenciaAno) ? competenciaAno : null,
      competenciaMes: Number.isFinite(competenciaMes) ? competenciaMes : null,
      dataCadastro: new Date().toISOString()
    };

    const personalPayload = {
      ...commonData,
      tipoConta: ACCOUNT_TYPE_PERSONAL,
      valor: amountValue,
      valorOriginal: amountValue,
      pagamentoGrupo: false,
      grupoId: null,
      formaDivisao: null,
      participacoes: []
    };

    const groupPayload = {
      ...commonData,
      tipoConta: ACCOUNT_TYPE_GROUP,
      grupoId,
      formaDivisao,
      valor: amountValue,
      valorOriginal: amountValue,
      ...(formaDivisao === DIVISION_CUSTOM ? { percentuais: effectivePercentuais } : {})
    };

    const payload = isGroupFlow ? groupPayload : personalPayload;

    if (isEditMode) {
      if (isEditingGroup) {
        const recreated = await saveDebitos(payload, false);
        if (!recreated) return;

        const removed = await excluirDebits(editId, false);
        if (!removed) {
          toast.error(t('couldNotRemoveOldDebt'));          
          return;
        }

       toast.success(t('groupExpenseUpdated'));

      } else {
        const updated = await updateDebitsValues({ ...payload, _id: editId });
        if (!updated) return;
      }
    } else {
      const created = await saveDebitos(payload);
      if (!created) return;
    }

    const nextVisibleMesRef =
      isValidMesRef(mesRefReferencia) && mesRefReferencia <= monthRange.end
        ? mesRefReferencia
        : currentMonth;

    if (nextVisibleMesRef < monthRange.start) {
      setOldestMonth(nextVisibleMesRef);
    }

    if (nextVisibleMesRef !== selectedMesRef) {
      setCompetencia(nextVisibleMesRef);
    }

    await Promise.all([
      getDebitos(user.id, { mesRef: nextVisibleMesRef }),
      getPendingApprovals({ mesRef: nextVisibleMesRef })
    ]);
    await refreshDebitDateRange();
    closeDialog();
  };

  const handleMarkAsPaid = async (id) => {
    const target = debitos.find((d) => getExpenseId(d) === id);
    const wasPaid = target?.situacao === 'Pago';

    const success = await payDebito(id);
    if (!success) {
      toast.error(t('failedToUpdateExpense'));
      return;
    }
    await reloadSelectedMonth();
    if (wasPaid) {
      toast.success(t('expenseUnmarkedAsPaid'));
    } else {
      toast.success(t('expenseMarkedAsPaid'));
    }
  };

  const handleApprovePending = async (debitId) => {
    const success = await approveParticipation(debitId);
    if (!success) {
      toast.error(t('failedToApproveParticipation'));
      return;
    }
    await reloadSelectedMonth();
    toast.success(t('participationApproved'));
  };

  const handleRejectPending = async (debitId) => {
    const success = await rejectParticipation(debitId);
    if (!success) {
      toast.error(t('failedToRejectParticipation'));
      return;
    }
    await reloadSelectedMonth();
    toast.success(t('participationRejected'));
  };

  const requestDelete = (expense) => {
    setDeleteTarget(expense || null);
  };

  const performDelete = async (scope) => {
    if (!deleteTarget) return;
    const id = getExpenseId(deleteTarget);
    if (!id) {
      setDeleteTarget(null);
      return;
    }

    const options = scope ? { scope } : {};
    const deleted = await excluirDebits(id, true, options);
    setDeleteTarget(null);
    if (!deleted) return;
    await reloadSelectedMonth();
    await refreshDebitDateRange();
  };

  const handleIsyCta = () => {
    if (!aiAnalysis?.cta) return;
    if (user?.plano === 'pro') {
      navigate('/investimentos');
    } else {
      setUpgradeProOpen(true);
    }
  };

  const handleProgressCta = () => {
    navigate('/dashboard');
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event, zoneKey, currentItems) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = currentItems.map((item) => getExpenseId(item)).filter(Boolean);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextOrder = arrayMove(ids, oldIndex, newIndex);
    setUserOrderById((prev) => ({ ...prev, [zoneKey]: nextOrder }));
  };

  const getApprovalBadge = (status) => {
    if (status === 'APROVADO') {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{t('approved')}</Badge>;
    }
    if (status === 'PENDENTE') {
      return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">{t('pending')}</Badge>;
    }
    if (status === 'RECUSADO') {
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{t('rejected')}</Badge>;
    }
    if (status === 'CONVIDADO_PENDENTE') {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{t('invited')}</Badge>;
    }
    return <Badge variant="outline">{status || '-'}</Badge>;
  };

  const formatDueDate = (value) => {
    if (!value) return '-';

    return new Date(value).toLocaleDateString(localeMap[language]);
  };

  const getRecurrenceLabel = (expense) => {
    if (expense.contaFixa === 0) {
      return t('fixed');
    }
    if (expense.contaFixa === 1) {
      return t('variable');
    }
    return t('installment');
  };

  const renderParticipations = (expense, participacoes, expenseId, mobile = false) => {
    if (!participacoes.length) {
      return null;
    }

    return (
      <div className={`space-y-2 rounded-md border border-slate-200 bg-slate-50 ${mobile ? 'mt-3 p-3' : 'p-2'}`}>
        {participacoes.map((participacao, index) => {
          const participantId = getParticipantId(participacao);

          return (
            <div
              key={participantId || `${expenseId || expense.descricao}-participant-${index}`}
              className={mobile
                ? 'space-y-2 rounded-md border border-slate-200 bg-white p-3'
                : 'flex items-center justify-between gap-2 text-xs'}
            >
              <div className={mobile ? 'min-w-0 space-y-1' : 'flex items-center gap-2'}>
                <span className="block break-words font-medium text-slate-700">
                  {participacao.nome || participacao.email}
                </span>
                <div className={mobile ? 'flex flex-col gap-1 text-xs text-slate-500' : 'contents'}>
                  <span>{formatCurrency(participacao.valorCalculado)}</span>
                  <span>{Number(participacao.percentual || 0).toFixed(2)}%</span>
                </div>
              </div>
              <div className={mobile ? 'pt-1' : ''}>
                {getApprovalBadge(participacao.statusAprovacao)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <AppLayout
      title={t('myExpenses')}
      ssubtitle={t('manageMonthlyExpenses')}
      icon={Wallet}
      headerActions={
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                onClick={() => openDialog()}
              >
                <Plus className="w-4 h-4 mr-2" />
               {t('addExpense')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {isEditMode
                        ? t('editExpense')
                        : t('addExpense')}
                </DialogTitle>
              </DialogHeader>
            
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('category')}</Label>
                 <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectCategory')} />
                </SelectTrigger>
                    <SelectContent>
                      {categoriasDropdown.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}

                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('description')}</Label>
                  <Input
                    placeholder={t('electricBillExample')}
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('debitType')}</Label>
                  <Select
                    value={tipoConta}
                    onValueChange={(value) => {
                      setTipoConta(value);
                      if (value !== ACCOUNT_TYPE_GROUP) {
                        setGrupoId('');
                        setGroupMembers([]);
                        setFormaDivisao(DIVISION_EQUAL);
                        setCustomPercentuais([]);
                      }
                    }}
                    disabled={isEditMode}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ACCOUNT_TYPE_PERSONAL}>{t('personal')}</SelectItem>
                      <SelectItem value={ACCOUNT_TYPE_GROUP}>{t('group')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {isEditMode && (
                    <p className="text-xs text-slate-500">
                      {t('debitTypeEditWarning')}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('totalAmountOriginal')}</Label>
                  <CurrencyInput
                    value={valor}
                    onValueChange={(val) => setValor(val)}
                    language={language}
                    placeholder={t('currencyPlaceholder')}
                  />
                </div>
                {tipoConta === ACCOUNT_TYPE_GROUP && (
                  <div className="space-y-3 rounded-md border border-slate-200 p-3">
                    <div className="space-y-2">
                      <Label>{t('selectGroup')}</Label>
                      <Select
                        value={grupoId}
                        onValueChange={(value) => {
                          setGrupoId(value);
                          setCustomPercentuais([]);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectGroup')} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableGroups.map((group) => (
                            <SelectItem key={group._id} value={String(group._id)}>
                              {group.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!availableGroups.length && (
                        <p className="text-xs text-amber-600">
                          {t('noGroupsAvailable')}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t('splitMethod')}</Label>
                      <Select
                        value={formaDivisao}
                        onValueChange={(value) => {
                          setFormaDivisao(value);
                          if (value === DIVISION_CUSTOM) {
                            setCustomPercentuais(equalPercentuais);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={DIVISION_EQUAL}>{t('equalPercentage')}</SelectItem>
                          <SelectItem value={DIVISION_CUSTOM}>{t('customPercentage')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {splitMembers.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t('participantsAndSplit')}</Label>
                        <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                          {splitMembers.map((member) => {
                            const memberId = String(member._id);
                            const effectivePercent = effectivePercentuais.find((item) => String(item.groupMemberId) === memberId)?.percentual || 0;
                            const preview = previewParticipacoes.find((item) => String(item.groupMemberId) === memberId);

                            return (
                              <div key={memberId} className="grid grid-cols-[1fr_120px_130px] items-center gap-2">
                                <div>
                                  <p className="text-xs font-medium text-slate-700">{member.nome || member.email}</p>
                                  <p className="text-[11px] text-slate-500">{member.email}</p>
                                </div>
                                {formaDivisao === DIVISION_CUSTOM ? (
                                  <Input
                                    type="number"
                                    value={effectivePercent}
                                    onChange={(event) => {
                                      const parsed = Number(event.target.value);
                                      setCustomPercentuais((prev) => {
                                        const base = prev.length > 0 ? [...prev] : [...equalPercentuais];
                                        return base.map((item) =>
                                          String(item.groupMemberId) === memberId
                                            ? { ...item, percentual: Number.isFinite(parsed) ? parsed : 0 }
                                            : item
                                        );
                                      });
                                    }}
                                  />
                                ) : (
                                  <div className="text-sm text-slate-700">{effectivePercent.toFixed(2)}%</div>
                                )}
                                <div className="text-sm font-medium text-slate-800">
                                  {formatCurrency(preview?.valorCalculado || 0)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {formaDivisao === DIVISION_CUSTOM && (
                      <div className="text-xs text-slate-600">
                        {t('percentagesSum', {total: totalPercentual.toFixed(2)})}
                      </div>
                    )}

                    {formaDivisao === DIVISION_CUSTOM && Math.abs(totalPercentual - 100) > 0.0001 && (
                      <p className="text-xs text-red-600">
                        {t('percentagesMustEqual100')}
                      </p>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                 <Label>{t('dueDate')}</Label>
                  <Input
                    type="date"
                    value={vdata}
                    onChange={(e) => setData(e.target.value)}
                  />
                </div>
                {isEditMode && (
                  <div className="space-y-2">
                    <Label>{t('status')}</Label>
                    <Select value={situacao} onValueChange={setSituacao}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {listSituacao.map((item) => (
                          <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t('recurrence')}</Label>
                  <Select value={contaFixa} onValueChange={setContaFixa}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t('fixed')}</SelectItem>
                      <SelectItem value="1">{t('variable')}</SelectItem>
                      <SelectItem value="2">{t('installment')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {contaFixa === '2' && (
                  <div className="space-y-2">
                    <Label><Label>{t('numberOfInstallments')}</Label></Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={qtdParcela}
                      onChange={(e) => setQtdParcela(e.target.value)}
                    />
                  </div>
                )}
                <Button className="w-full" onClick={handleSubmit}>
                  {isEditMode ? t('update') : t('add')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
      }
      mainClassName="space-y-6 debits-page"
    >
          {/* Bloco superior unificado: Isy + Resumo do mês */}
          <section className={`debits-hero debits-hero--${aiAnalysis.tone}`}>
            <div className="debits-hero__isy">
              <div className="debits-hero__isy-glow" aria-hidden="true" />
              <div className="debits-hero__sparkles" aria-hidden="true">
                <span className="spark spark--1" />
                <span className="spark spark--2" />
                <span className="spark spark--3" />
                <span className="spark spark--4" />
                <span className="spark spark--5" />
              </div>
              <div className="debits-hero__isy-inner">
                <img
                  src={aiAnalysis.avatar}
                  alt="Isy"
                  className="debits-hero__avatar"
                />
                <div className="debits-hero__isy-content">
                  <span className="debits-hero__isy-tag">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    Isy
                  </span>
                  <h2 className="debits-hero__isy-title">{aiAnalysis.titulo}</h2>
                  <p className="debits-hero__isy-message">{aiAnalysis.mensagem}</p>
                  {aiAnalysis.cta && (
                    <button
                      type="button"
                      className="debits-hero__isy-cta"
                      onClick={handleIsyCta}
                    >
                      {aiAnalysis.cta.label}
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="debits-hero__divider" aria-hidden="true" />

            <div className="debits-hero__summary">
              <div className="debits-hero__summary-head">
                <h3 className="debits-hero__summary-title">
                  {t('monthSummaryTitle', { month: competenciaFormatada })}
                </h3>
                <div className="debits-hero__month-nav">
                  <button
                    type="button"
                    className="debits-month-btn"
                    onClick={() => changeCompetenciaBy(-1)}
                    disabled={!canGoToPreviousMonth}
                    aria-label={t('previousMonth')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <Select value={selectedMesRef} onValueChange={handleCompetenciaChange}>
                    <SelectTrigger className="debits-month-select" aria-label={t('selectMonth')}>
                      <SelectValue placeholder={t('selectMonth')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {competenciaOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    className="debits-month-btn"
                    onClick={() => changeCompetenciaBy(1)}
                    disabled={!canGoToNextMonth}
                    aria-label={t('nextMonth')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="debits-hero__mini-grid">
                <div className="debits-mini-card debits-mini-card--income">
                  <div className="debits-mini-card__icon" aria-hidden="true">
                    <DollarSign className="h-4 w-4" />
                  </div>
                  <span className="debits-mini-card__label">{t('monthlyIncomeCard')}</span>
                  <span className="debits-mini-card__value">{formatCurrency(metrics.receita)}</span>
                </div>
                <div className="debits-mini-card debits-mini-card--paid">
                  <div className="debits-mini-card__icon" aria-hidden="true">
                    <Check className="h-4 w-4" />
                  </div>
                  <span className="debits-mini-card__label">{t('paid')}</span>
                  <span className="debits-mini-card__value">{formatCurrency(metrics.totalPago)}</span>
                </div>
                <div className="debits-mini-card debits-mini-card--pending">
                  <div className="debits-mini-card__icon" aria-hidden="true">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <span className="debits-mini-card__label">{t('monthlyToPay')}</span>
                  <span className="debits-mini-card__value">{formatCurrency(metrics.totalPendente)}</span>
                </div>
              </div>

              {(() => {
                const ratio = metrics.receita > 0
                  ? Math.min(1, metrics.totalGastos / metrics.receita)
                  : 0;
                const percent = Math.round(ratio * 100);
                return (
                  <div className="debits-progress">
                    <div className="debits-progress__circle" aria-hidden="true">
                      <svg viewBox="0 0 36 36" className="debits-progress__ring">
                        <path
                          className="debits-progress__ring-bg"
                          d="M18 2.5a15.5 15.5 0 1 1 0 31 15.5 15.5 0 0 1 0-31"
                        />
                        <path
                          className="debits-progress__ring-fill"
                          d="M18 2.5a15.5 15.5 0 1 1 0 31 15.5 15.5 0 0 1 0-31"
                          strokeDasharray={`${ratio * 97.39}, 97.39`}
                        />
                      </svg>
                      <span className="debits-progress__circle-text">{percent}%</span>
                    </div>
                    <div className="debits-progress__info">
                      <span className="debits-progress__title">{t('monthExpensesProgress')}</span>
                      <span className="debits-progress__detail">
                        {t('expensesProgressDetail', {
                          spent: formatCurrency(metrics.totalGastos),
                          income: formatCurrency(metrics.receita),
                        })}
                      </span>
                      <div className="debits-progress__bar">
                        <span
                          className="debits-progress__bar-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="debits-progress__cta"
                      onClick={handleProgressCta}
                    >
                      {t('seeFullAnalysis')}
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                );
              })()}
            </div>
          </section>

          {pendingApprovals?.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {t('pendingApprovals')}
              </h3>
              <Badge variant="outline">
                {pendingApprovals.length}
              </Badge>
            </div>
            {(
              <div className="space-y-2">
                {pendingApprovals.map((debit) => {
                  const myParticipation = Array.isArray(debit.participacoes) ? debit.participacoes[0] : null;
                  return (
                    <div
                      key={debit._id}
                      className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-slate-800">{debit.descricao}</p>
                        <p className="text-xs text-slate-600">
                         {t('total')}: {formatCurrency(debit.valorOriginal || debit.valor)} |{' '}
                          {t('yourShare')}: {formatCurrency(myParticipation?.valorCalculado || debit.valor)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleRejectPending(debit._id)}>
                          {t('reject')}
                        </Button>
                        <Button size="sm" onClick={() => handleApprovePending(debit._id)}>
                          {t('approve')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          )}

          {/* Toolbar moderno */}
          <div className="debits-toolbar">
            <span className="debits-toolbar__label">
              {t('showingMonth', { month: competenciaFormatada })}
            </span>
            <div className="debits-toolbar__actions">
              <Select value={filtroSituacao} onValueChange={setFiltroSituacao}>
                <SelectTrigger
                  className="debits-toolbar__select debits-toolbar__select--status"
                  aria-label={t('status')}
                >
                  <SelectValue placeholder={t('all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">{t('all')}</SelectItem>
                  <SelectItem value="pendente">{t('pending')}</SelectItem>
                  <SelectItem value="pago">{t('paid')}</SelectItem>
                  <SelectItem value="atrasado">{t('overdue')}</SelectItem>
                </SelectContent>
              </Select>

              <div className="debits-toolbar__sort">
                <ArrowUpDown className="h-4 w-4 debits-toolbar__sort-icon" aria-hidden="true" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="debits-toolbar__select" aria-label={t('sortByLabel')}>
                    <SelectValue placeholder={t('sortByLabel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t('sortDefault')}</SelectItem>
                    <SelectItem value="dueDate">{t('dueDate')}</SelectItem>
                    <SelectItem value="amount">{t('totalAmountOriginal')}</SelectItem>
                    <SelectItem value="category">{t('category')}</SelectItem>
                    <SelectItem value="description">{t('description')}</SelectItem>
                    <SelectItem value="status">{t('status')}</SelectItem>
                    <SelectItem value="recurrence">{t('recurrence')}</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  className="debits-toolbar__sort-dir"
                  onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  disabled={sortBy === 'default'}
                  aria-label={sortDirection === 'asc' ? t('sortAscending') : t('sortDescending')}
                  title={sortDirection === 'asc' ? t('sortAscending') : t('sortDescending')}
                >
                  {sortDirection === 'asc' ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : (
                    <ArrowDown className="h-4 w-4" />
                  )}
                </button>
              </div>

              <div className="debits-toolbar__view-toggle" role="group" aria-label={t('viewList')}>
                <button
                  type="button"
                  className={`debits-toolbar__view-btn${viewMode === 'list' ? ' is-active' : ''}`}
                  aria-pressed={viewMode === 'list'}
                  aria-label={t('viewList')}
                  onClick={() => setViewMode('list')}
                  title={t('viewList')}
                >
                  <ListIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className={`debits-toolbar__view-btn${viewMode === 'grid' ? ' is-active' : ''}`}
                  aria-pressed={viewMode === 'grid'}
                  aria-label={t('viewGrid')}
                  onClick={() => setViewMode('grid')}
                  title={t('viewGrid')}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Lista de despesas (cards) */}
          {isLoadingDebitos ? (
            <div className="expense-empty">
              <p>{t('loadingExpenses')}</p>
            </div>
          ) : loadError ? (
            <div className="expense-empty expense-empty--error">
              <p className="expense-empty__title">{t('expensesLoadErrorTitle')}</p>
              <p className="expense-empty__detail">{t('expensesLoadErrorDetail')}</p>
              <Button
                type="button"
                className="expense-empty__cta"
                onClick={() => reloadSelectedMonth()}
              >
                {t('tryAgain')}
              </Button>
            </div>
          ) : debitosFiltrados.length === 0 ? (
            <div className="expense-empty">
              <p className="expense-empty__title">{t('noExpensesYet')}</p>
              <Button
                type="button"
                className="expense-empty__cta"
                onClick={() => openDialog()}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {t('addFirstExpense')}
              </Button>
            </div>
          ) : (
            <LayoutGroup>
              <div className={`expense-list expense-list--${viewMode}`} data-view={viewMode}>
                {(() => {
                  const renderZone = (items, zoneKey, zoneClass) => {
                    if (!items.length) return null;
                    const ids = items.map((item) => getExpenseId(item)).filter(Boolean);
                    return (
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleDragEnd(e, zoneKey, items)}
                      >
                        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                          <div className={`expense-list__zone ${zoneClass}`}>
                            {items.map((expense) => {
                              const expenseId = getExpenseId(expense);
                              if (!expenseId) return null;
                              const isGroupExpense =
                                expense.tipoConta === ACCOUNT_TYPE_GROUP || Boolean(expense.pagamentoGrupo);
                              const participacoes = Array.isArray(expense.participacoes)
                                ? expense.participacoes
                                : [];
                              const isOwner = String(expense.userId) === String(user?.id);
                              const installmentInfo = getInstallmentInfo(expense);

                              return (
                                <SortableExpenseCard
                                  key={expenseId}
                                  expense={expense}
                                  expenseId={expenseId}
                                  isOwner={isOwner}
                                  isGroupExpense={isGroupExpense}
                                  participacoes={participacoes}
                                  installmentInfo={installmentInfo}
                                  dragEnabled={dragEnabled}
                                  t={t}
                                  formatCurrency={formatCurrency}
                                  formatDueDate={formatDueDate}
                                  getRecurrenceLabel={getRecurrenceLabel}
                                  onEdit={openDialog}
                                  onTogglePay={handleMarkAsPaid}
                                  onDelete={requestDelete}
                                  renderParticipations={renderParticipations}
                                />
                              );
                            })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    );
                  };

                  if (useZones) {
                    return (
                      <>
                        {renderZone(pendingItems, 'pending', 'expense-list__zone--pending')}
                        {renderZone(paidItems, 'paid', 'expense-list__zone--paid')}
                      </>
                    );
                  }
                  return renderZone(flatItems, 'flat', 'expense-list__zone--flat');
                })()}
              </div>
            </LayoutGroup>
          )}

          <UpgradeProModal
            open={upgradeProOpen}
            onOpenChange={setUpgradeProOpen}
          />

          <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {t('deleteExpense')}
                </DialogTitle>
                <DialogDescription>
                  {(() => {
                    const tipo = Number(deleteTarget?.contaFixa);
                    const desc = deleteTarget?.descricao || '';
                    if (tipo === 2) {
                      return t('deleteInstallmentMessage', { desc })
                    }
                    if (tipo === 0) {
                      return t('deleteFixedExpenseMessage', { desc })
                    }
                    return t('confirmDeleteExpense', { desc })
                  })()}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                 {t('cancel')}
                </Button>
                {Number(deleteTarget?.contaFixa) === 2 ? (
                  <>
                    <Button variant="outline" onClick={() => performDelete('one')}>
                     {t('onlyThisInstallment')}
                    </Button>
                    <Button variant="destructive" onClick={() => performDelete('all')}>
                      {t('entireRecurrence')}
                    </Button>
                  </>
                ) : Number(deleteTarget?.contaFixa) === 0 ? (
                  <>
                    <Button variant="outline" onClick={() => performDelete('one')}>
                     {t('onlyThisMonth')}
                    </Button>
                    <Button variant="destructive" onClick={() => performDelete('all')}>
                      {t('thisAndFutureMonths')}
                    </Button>
                  </>
                ) : (
                  <Button variant="destructive" onClick={() => performDelete()}>
                   {t('delete')}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
    </AppLayout>
  );
}

export default Debits;
